import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import type { DebounceSettings } from 'lodash';
import type { ActionCreator, AnyAction } from 'redux';

import useDebouncedCallback from '../../../../hooks/useDebouncedCallback';
import * as DependenciesV2Store from '../../../../redux/dependenciesV2';

const DEBOUNCE_TIMEOUT = 750;

//  We want requests to happen after the last user action takes place.
//  Read more: https://css-tricks.com/debouncing-throttling-explained-examples/#aa-debounce
const DEBOUNCE_OPTIONS: DebounceSettings = {
  leading: false,
  trailing: true,
};

const useFetchDependencyAction = (actionCreator: ActionCreator<AnyAction>) => {
  const dispatch = useDispatch();

  const dispatchFetchDependencies = useDebouncedCallback(() => {
    dispatch(DependenciesV2Store.actions.fetchDependencies());
  }, DEBOUNCE_TIMEOUT, DEBOUNCE_OPTIONS);

  const fetchDependencies = useCallback((...args: unknown[]) => {
    /**
     * First we dispatch the initial action,
     * which will update the Redux state immediately.
     */
    dispatch(actionCreator(...args));

    /**
     * Because the next async fetch action is debounced, the UI will not appear
     * that it is loading until the debounce function is invoked,
     * which will be 750ms after the last user action.
     *
     * To avoid this, we first dispatch an action so that the UI will
     * show the loading UI immediately.
     */
    dispatch(DependenciesV2Store.actions.preFetchLoading());

    /**
     * ...then we dispatch the fetchDependencies
     *
     * Note that Redux actions are synchronous by default, meaning that the reducer will
     * have been called from the previous action's dispatch.
     * This is how the fetchProjectsAction (an async thunk) is able to read the
     * latest state and use that to make the request.
     */
    dispatchFetchDependencies();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionCreator]);

  return fetchDependencies;
};

export default useFetchDependencyAction;
